import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { anchorMissing, ensureProjectsView, presentAnchors } from './tasks-help.shared';

/**
 * ?-Hilfe der Ansicht „Aufträge“ in `/tasks/overview?view=projects` (08.10.2026).
 *
 * Das ? der Uebersicht waehlt nach Ansicht: Liste/Board/Tag → `tasks-overview-help`,
 * „Aufträge“ → diese. Die Aufgaben-Tour hat schon zehn Stationen und erklaert Pillen,
 * Sichten und Zeilen, die es in „Aufträge“ nicht gibt.
 *
 * Erklaert wird EINE Zeile: die Zeilen-Anker sitzen nur an der ersten sichtbaren Zeile
 * (`tourAnchor()` in `project-list-view.component.ts`). Ohne Auftrag springen die
 * Zeilenstationen; die Begruessung nennt dann, wie man den ersten anlegt.
 *
 * Kernaussagen:
 *  - **Eine Zeile = ein Auftrag**, „Jetzt bei“ sagt, wo er gerade liegt.
 *  - **„Termin gefährdet“ ist eine Rechnung** (weniger als 24 Std., offene Soll-Zeit groesser
 *    als die Restzeit, oder ein Schritt nach dem Termin faellig) — der Grund steht im Tooltip.
 *  - **Titel oeffnet die Auftragsuebersicht**, aufklappen zeigt die Schritte an Ort und Stelle.
 */
const page = { placement: 'bottom' as const, nextCondition: 'manual' as const, showNextButton: true };

export const TASKS_PROJECTS_VIEW_HELP_TOUR: TourConfig = {
  id: TourFeature.TasksProjectsViewHelp,
  version: 1,
  title: 'TASKS_PROJECTS_VIEW_HELP_TOUR_TITLE',
  description: 'TASKS_PROJECTS_VIEW_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Ohne Query: die Seite übernimmt ?view= nur beim ersten Aufbau; `prepare` schaltet um.
  launchContext: { route: '/tasks/overview', prepare: () => ensureProjectsView(), mode: 'auto' },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 170,
  category: 'feature-discovery',
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'TASKS_PROJECTS_VIEW_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'pv-welcome',
          anchor: AnchorId.TasksViewSwitch,
          spotlightAnchor: AnchorId.TasksViewSwitch,
          title: 'TUTORIAL.TASKS_PROJECTS_VIEW.WELCOME.TITLE',
          content: 'TUTORIAL.TASKS_PROJECTS_VIEW.WELCOME.CONTENT',
          ...page,
          showPrevButton: false,
        },
        {
          id: 'pv-filters',
          anchor: AnchorId.TasksProjectState,
          spotlightAnchor: AnchorId.TasksProjectState,
          get additionalSpotlightAnchors() {
            return presentAnchors([
              AnchorId.TasksFilterDepartment,
              AnchorId.TasksFilterPerson,
              AnchorId.TasksProjectGroupMenu,
            ]);
          },
          title: 'TUTORIAL.TASKS_PROJECTS_VIEW.FILTERS.TITLE',
          content: 'TUTORIAL.TASKS_PROJECTS_VIEW.FILTERS.CONTENT',
          ...page,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksProjectState),
        },
        {
          id: 'pv-groups',
          anchor: AnchorId.TasksProjectGroup,
          spotlightAnchor: AnchorId.TasksProjectGroup,
          title: 'TUTORIAL.TASKS_PROJECTS_VIEW.GROUPS.TITLE',
          content: 'TUTORIAL.TASKS_PROJECTS_VIEW.GROUPS.CONTENT',
          ...page,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksProjectGroup),
        },
        {
          id: 'pv-row',
          anchor: AnchorId.TasksProjectRowMeta,
          spotlightAnchor: AnchorId.TasksProjectRow,
          title: 'TUTORIAL.TASKS_PROJECTS_VIEW.ROW.TITLE',
          content: 'TUTORIAL.TASKS_PROJECTS_VIEW.ROW.CONTENT',
          ...page,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksProjectRowMeta),
        },
        {
          id: 'pv-who',
          anchor: AnchorId.TasksProjectRowWho,
          spotlightAnchor: AnchorId.TasksProjectRowWho,
          title: 'TUTORIAL.TASKS_PROJECTS_VIEW.WHO.TITLE',
          content: 'TUTORIAL.TASKS_PROJECTS_VIEW.WHO.CONTENT',
          ...page,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksProjectRowWho),
        },
        {
          // Termin + Fortschritt stehen nur in der Tabelle (ab 1024 px) als eigene Zellen.
          id: 'pv-progress',
          anchor: AnchorId.TasksProjectRowProgress,
          spotlightAnchor: AnchorId.TasksProjectRowProgress,
          additionalSpotlightAnchors: [AnchorId.TasksProjectRowDue],
          title: 'TUTORIAL.TASKS_PROJECTS_VIEW.PROGRESS.TITLE',
          content: 'TUTORIAL.TASKS_PROJECTS_VIEW.PROGRESS.CONTENT',
          ...page,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksProjectRowProgress),
        },
        {
          id: 'pv-expand',
          anchor: AnchorId.TasksProjectRowExpand,
          spotlightAnchor: AnchorId.TasksProjectRowExpand,
          title: 'TUTORIAL.TASKS_PROJECTS_VIEW.EXPAND.TITLE',
          content: 'TUTORIAL.TASKS_PROJECTS_VIEW.EXPAND.CONTENT',
          ...page,
          placement: 'right',
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksProjectRowExpand),
        },
        {
          id: 'pv-open',
          anchor: AnchorId.TasksProjectRowOpen,
          spotlightAnchor: AnchorId.TasksProjectRowOpen,
          title: 'TUTORIAL.TASKS_PROJECTS_VIEW.OPEN.TITLE',
          content: 'TUTORIAL.TASKS_PROJECTS_VIEW.OPEN.CONTENT',
          ...page,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksProjectRowOpen),
        },
        {
          id: 'pv-menu',
          anchor: AnchorId.TasksProjectRowMenu,
          spotlightAnchor: AnchorId.TasksProjectRowMenu,
          title: 'TUTORIAL.TASKS_PROJECTS_VIEW.MENU.TITLE',
          content: 'TUTORIAL.TASKS_PROJECTS_VIEW.MENU.CONTENT',
          ...page,
          placement: 'left',
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksProjectRowMenu),
        },
        {
          id: 'pv-create',
          anchor: AnchorId.TasksCreateButton,
          spotlightAnchor: AnchorId.TasksCreateButton,
          title: 'TUTORIAL.TASKS_PROJECTS_VIEW.CREATE.TITLE',
          content: 'TUTORIAL.TASKS_PROJECTS_VIEW.CREATE.CONTENT',
          ...page,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksCreateButton),
          actions: [
            {
              labelKey: 'TUTORIAL.TASKS_PROJECTS_VIEW.CREATE.ACTION',
              icon: 'fa-light fa-circle-question',
              startTour: TourFeature.TaskProjectSheetHelp,
              variant: 'secondary',
            },
          ],
        },
      ],
    },
  ],
};
